/**
 * Real squircle corners, ported from dorahi-main's components/ui/squircle.tsx
 * + lib/squircle.ts (Apple-style bezier approximation).
 *
 * The theme used to lean on `corner-shape: squircle`, which only lands in very
 * recent Chrome/Safari — everywhere else the corners fell back to a plain
 * border-radius. This applies the same clip-path the app generated:
 *
 *   1. read the element's border-radius (from the `rounded-[Npx]` utility, so
 *      the responsive `rounded-[40px] mobile:rounded-[30px]` pairs keep
 *      working) and its box size
 *   2. clip the element to the squircle path
 *   3. if the element has a visible border, draw it back as an SVG overlay
 *      stroke that follows the same path, because clip-path cuts the CSS
 *      border off (this is exactly what the React component did)
 *
 * Elements are tracked with a ResizeObserver and re-scanned on DOM changes.
 * This runs in every browser (not just ones with `corner-shape`) so the
 * corners look the same everywhere.
 */

// lib/squircle.ts — squircleBezierApproximation
const BEZIER = [
  [
    [0.3, 0],
    [0.473, 0],
    [0.619, 0.039],
  ],
  [
    [0.804, 0.088],
    [0.912, 0.196],
    [0.961, 0.381],
  ],
  [
    [1, 0.527],
    [1, 0.7],
    [1, 1],
  ],
];

const vecAdd = ([x1, y1], [x2, y2]) => [x1 + x2, y1 + y2];
const vecMul = ([x, y], k) => [x * k, y * k];

function rotBy90([x, y], k) {
  switch (k % 4) {
    case 1:
      return [y, -x];
    case 2:
      return [-x, -y];
    case 3:
      return [-y, x];
    default:
      return [x, y];
  }
}

/** getSquirclePath(width, height, r) from lib/squircle.ts */
function getSquirclePath(width, height, r) {
  if (!width || !height) return "";
  const radius = Math.min(r, width / 2, height / 2);
  const [left, top, right, bottom] = [0, 0, width, height];

  let path = `M ${left + radius} ${top}`;

  const corners = [
    { start: [right - radius, top], rot: 0 },
    { start: [right, bottom - radius], rot: 3 },
    { start: [left + radius, bottom], rot: 2 },
    { start: [left, top + radius], rot: 1 },
  ];

  for (const { start, rot } of corners) {
    path += ` L ${start[0]} ${start[1]}`;
    for (const [cA, cB, target] of BEZIER) {
      const a = vecAdd(start, vecMul(rotBy90(cA, rot), radius));
      const b = vecAdd(start, vecMul(rotBy90(cB, rot), radius));
      const t = vecAdd(start, vecMul(rotBy90(target, rot), radius));
      path += ` C ${a[0]} ${a[1]}, ${b[0]} ${b[1]}, ${t[0]} ${t[1]}`;
    }
  }

  return `${path} Z`;
}

const SELECTOR = ".rounded-squircle";

// Runs everywhere rather than deferring to `corner-shape: squircle`, so the
// corners are identical in every browser instead of only the newest ones.
const supportsClipPath = () => {
  try {
    return (
      typeof CSS !== "undefined" &&
      CSS.supports &&
      CSS.supports("clip-path", 'path("M0 0")')
    );
  } catch (error) {
    return false;
  }
};

if (!window.__dorahiSquircle && supportsClipPath()) {
  window.__dorahiSquircle = true;

  const tracked = new WeakSet();
  const observer =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver((entries) =>
          entries.forEach((entry) => paint(entry.target)),
        )
      : null;

  function paint(element) {
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    if (!width || !height) return;

    const styles = getComputedStyle(element);

    // Safari ignores clip-path on inline boxes entirely (Chrome applies it),
    // so inline elements — e.g. <a> pills — silently fall back to plain
    // border-radius on iPhones. Promote them to inline-block before clipping.
    if (styles.display === "inline") element.style.display = "inline-block";

    // paint() writes inline overrides (transparent border, zeroed radius —
    // see below). Clear them before reading, or repaints read back their own
    // writes. Transitions must be off while they're cleared: `transition-all`
    // elements otherwise animate the round-trip and reads land mid-animation.
    // `styles` is live, so reads below reflect these changes.
    const inlineTransition = element.style.getPropertyValue("transition");
    const restoreTransition = () => {
      if (inlineTransition) {
        element.style.setProperty("transition", inlineTransition);
      } else {
        element.style.removeProperty("transition");
      }
    };
    element.style.setProperty("transition", "none", "important");
    element.style.removeProperty("border-color");
    element.style.removeProperty("border-radius");

    // Largest corner radius wins, matching the single `radius` prop the React
    // Squircle took (the utilities only ever set one uniform radius).
    const radius = Math.max(
      parseFloat(styles.borderTopLeftRadius) || 0,
      parseFloat(styles.borderTopRightRadius) || 0,
      parseFloat(styles.borderBottomLeftRadius) || 0,
      parseFloat(styles.borderBottomRightRadius) || 0,
    );
    if (!radius) return restoreTransition();

    const path = getSquirclePath(width, height, radius);
    if (!path) return restoreTransition();

    element.style.clipPath = `path('${path}')`;
    element.style.webkitClipPath = `path('${path}')`;

    // Without corner-shape support (Safari, Firefox) the CSS border-radius
    // still rounds the paint under the clip. A clip only removes pixels, and
    // circular corners are tighter than squircle ones, so the border-radius
    // shape wins visually and the squircle never shows. Zero it so the clip
    // alone defines the shape — what the React Squircle did (it clipped an
    // unrounded box).
    element.style.setProperty("border-radius", "0px", "important");

    const borderWidth = parseFloat(styles.borderTopWidth) || 0;
    let overlay = element.querySelector(":scope > [data-squircle-border]");

    if (borderWidth > 0 && styles.borderTopStyle !== "none") {
      if (getComputedStyle(element).position === "static")
        element.style.position = "relative";

      if (!overlay) {
        overlay = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        overlay.setAttribute("data-squircle-border", "");
        overlay.setAttribute("aria-hidden", "true");
        overlay.style.cssText =
          "position:absolute;top:-1px;left:-1px;width:100%;height:100%;pointer-events:none;z-index:10;overflow:visible";
        const svgPath = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "path",
        );
        svgPath.setAttribute("fill", "none");
        svgPath.setAttribute("vector-effect", "non-scaling-stroke");
        overlay.appendChild(svgPath);
        element.appendChild(overlay);
      }

      const svgPath = overlay.firstElementChild;
      svgPath.setAttribute("d", path);
      svgPath.setAttribute("stroke", styles.borderTopColor);
      // clip-path cuts the outer half of the stroke, so double it (as in squircle.tsx).
      svgPath.setAttribute("stroke-width", String(borderWidth * 2));

      // The CSS border would still paint at the wrong (border-radius) shape
      // under the stroke. Hide it with !important so it also beats
      // !border-[...] utilities (e.g. the selected size button).
      element.style.setProperty("border-color", "transparent", "important");
    } else if (overlay) {
      overlay.remove();
    }

    // Restore transitions (border-color is already transparent again, so
    // nothing animates from this).
    restoreTransition();
  }

  function scan(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach((element) => {
      paint(element);
      if (!tracked.has(element)) {
        tracked.add(element);
        observer?.observe(element);
      }
    });
  }

  const scheduleScan = () => requestAnimationFrame(() => scan());

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleScan);
  } else {
    scheduleScan();
  }

  // Fonts/images settling changes box sizes; re-measure once they're in.
  window.addEventListener("load", scheduleScan);
  document.fonts?.ready?.then(scheduleScan);
  window.addEventListener("resize", scheduleScan);

  // Sections re-rendered by the cart/collection/review code, theme editor, etc.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Class swaps change border color without resizing (e.g. the size
      // buttons toggling border-[#d1d5dc] / !border-[#fd184d] on selection);
      // repaint so the stroke picks up the new color.
      if (mutation.type === "attributes") {
        if (mutation.target.matches?.(SELECTOR)) paint(mutation.target);
        continue;
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          if (node.matches?.(SELECTOR)) paint(node);
          scan(node);
        }
      }
      // innerHTML re-renders (variant state, cart refresh) wipe the border
      // overlay without resizing the element; redraw it when that happens.
      for (const node of mutation.removedNodes) {
        if (node.nodeType === 1 && node.hasAttribute?.("data-squircle-border")) {
          const host =
            mutation.target.nodeType === 1 &&
            (mutation.target.closest?.(SELECTOR) || null);
          if (host) paint(host);
        }
      }
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    // class only — paint() writes to `style`, so observing style would loop.
    attributeFilter: ["class"],
  });

  window.addEventListener("dorahi:squircle:refresh", scheduleScan);
}
